/**
 * FN-FE-06 — Cikktörzs UI (cikkek, import wizard, kategóriák, árlisták)
 */
import { useState, useEffect, type Dispatch, type SetStateAction } from 'react';
import { get, post, del, patch } from '../../lib/api';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import PageHeader from '../../components/ui/PageHeader';
import EmptyState from '../../components/ui/EmptyState';
import Tabs from '../../components/ui/Tabs';
import MoneyInput from '../../components/ui/MoneyInput';
import ActionsDropdown from '../../components/ActionsDropdown';
import { ScannerButton } from '../../components/BarcodeScanner';
import { useToast } from '../../components/ui/Toast';
import { useFeatureContext } from '../../contexts/FeatureContext';
import { useFKeyBar } from '../../components/layout/FKeyBar';
import { ProductBuilderModal } from './ProductBuilderModal';

interface Item {
  id: string; sku: string; name: string; kind: string; unit: string; unitNavCode?: string | null;
  netUnitPrice: number; vatRate: string; categoryId?: string; isArchived: boolean;
  imageUrl?: string | null;
}

interface Variant {
  id: string; parentItemId: string; variantSuffix: string | null; variantSku: string | null;
  variantName: string; sortOrder: number; imageUrl: string | null;
  netUnitPriceOverride: number | null; purchasePriceOverride: number | null;
}

interface KonfigInput {
  id: string; itemId: string; kod: string; nev: string;
  tipus: 'LISTA' | 'SZAM' | 'SZOVEG';
  opciok: string[] | null; egyseg: string | null;
  minErtek: number | null; maxErtek: number | null;
  minFuggoInputKod: string | null; maxFuggoErtek: Record<string, number> | null;
  sortOrder: number;
}

// NAV szabvány egységek — kód + megjelenítendő rövidítés + hosszú név
const NAV_UNITS = [
  { code: 'PIECE',          abbr: 'db',    label: 'Darab' },
  { code: 'KGM',            abbr: 'kg',    label: 'Kilogramm' },
  { code: 'MTR',            abbr: 'm',     label: 'Méter' },
  { code: 'M2',             abbr: 'm²',    label: 'Négyzetméter' },
  { code: 'M3',             abbr: 'm³',    label: 'Köbméter' },
  { code: 'LTR',            abbr: 'l',     label: 'Liter' },
  { code: 'HOUR',           abbr: 'h',     label: 'Óra' },
  { code: 'MINUTE',         abbr: 'perc',  label: 'Perc' },
  { code: 'DAY',            abbr: 'nap',   label: 'Nap' },
  { code: 'WEEK',           abbr: 'hét',   label: 'Hét' },
  { code: 'MONTH',          abbr: 'hó',    label: 'Hónap' },
  { code: 'YEAR',           abbr: 'év',    label: 'Év' },
  { code: 'KILOWATT_HOUR',  abbr: 'kWh',   label: 'Kilowatt-óra' },
  { code: 'OWN',            abbr: '',      label: 'Egyéb (saját)' },
] as const;

export default function ItemsPage() {
  const { activeFeatures } = useFeatureContext();
  const hasMultiplePriceLists = activeFeatures.includes('tobb_arlista');
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);
  const [builderEditItem, setBuilderEditItem] = useState<Item | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [editItem, setEditItem] = useState<Item | null>(null);
  const [imageUploadItem, setImageUploadItem] = useState<Item | null>(null);

  useFKeyBar([
    { key: 'F5', label: 'Új cikk', action: () => { setEditItem(null); setShowNewModal(true); } },
    { key: 'F7', label: 'Import', action: () => setShowImport(true) },
  ]);

  async function loadItems() {
    setLoading(true);
    try {
      const q = search ? `?search=${encodeURIComponent(search)}` : '';
      const data = await get<any>(`/items${q}`);
      setItems(data.data || data);
    } catch { /* */ }
    setLoading(false);
  }

  useEffect(() => { loadItems(); }, []);
  useEffect(() => { const t = setTimeout(loadItems, 300); return () => clearTimeout(t); }, [search]);

  function formatPrice(huf: string | number): string {
    return Math.round(Number(huf)).toLocaleString('hu-HU') + ' Ft';
  }

  const itemsContent = (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 14 }}>
        <Button variant="ghost" size="sm" onClick={() => setShowImport(true)}>Import CSV/XLSX</Button>
        <Button variant="ghost" size="sm" onClick={() => setShowNewModal(true)}>+ Alap cikk</Button>
        <Button size="sm" onClick={() => setShowBuilder(true)}>+ Termék Építő</Button>
      </div>
      {/* Szűrő-sáv */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
        <Input placeholder="Keresés SKU, név alapján..." value={search} onChange={e => setSearch(e.target.value)} className="max-w-xs" />
        <ScannerButton onScan={(code) => setSearch(code)} size="sm" />
      </div>
      {loading ? (
        <div className="space-y-3">{[1,2,3,4,5].map(i => <div key={i} className="h-12 bg-gray-100 animate-pulse rounded" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState icon="🛒" title="Még nincsenek cikkek" description="Hozz létre egyet a + Új cikk gombbal, vagy importálj CSV/XLSX-ből." action={<Button size="sm" onClick={() => setShowNewModal(true)}>+ Új cikk</Button>} />
      ) : (
        <div className="bg-white rounded-xl border border-[var(--color-border)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <th className="px-4 py-3" style={{ width: 48 }}>Kép</th>
                <th className="px-4 py-3">SKU</th>
                <th className="px-4 py-3">Megnevezés</th>
                <th className="px-4 py-3">Típus</th>
                <th className="px-4 py-3">Nettó egységár</th>
                <th className="px-4 py-3">ÁFA</th>
                <th className="px-4 py-3">Műveletek</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-2 py-2" style={{ width: 48 }}>
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt={item.name} style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--line)' }} />
                    ) : (
                      <div style={{ width: 36, height: 36, borderRadius: 6, background: 'var(--mist)', border: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>📦</div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{item.sku}</td>
                  <td className="px-4 py-3 font-medium text-[#1a2540]">{item.name}</td>
                  <td className="px-4 py-3">
                    <Badge variant={item.kind === 'product' ? 'info' : 'default'}>{item.kind === 'product' ? 'Termék' : 'Szolgáltatás'}</Badge>
                  </td>
                  <td className="px-4 py-3">{formatPrice(item.netUnitPrice)}</td>
                  <td className="px-4 py-3 text-gray-600">{item.vatRate}%</td>
                  <td className="px-4 py-3">
                    <ActionsDropdown actions={[
                      { label: 'Szerkesztés', icon: '✏️', onClick: () => { setBuilderEditItem(item); setShowBuilder(true); } },
                      { separator: true, label: '', onClick: () => {} },
                      { label: 'Archiválás', icon: '🗄', onClick: async () => {
                        try { await post(`/items/${item.id}/archive`, {}); loadItems(); } catch { /* */ }
                      }, danger: true },
                    ]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );

  return (
    <div>
      <PageHeader title="Cikkek" description="Termékek és szolgáltatások nyilvántartása." />

      <Tabs tabs={[
        { label: 'Cikkek', content: itemsContent },
        { label: 'Árlisták', content: <PriceListsTab hasMultiplePriceLists={hasMultiplePriceLists} /> },
      ]} />

      {/* Új cikk modal */}
      {showNewModal && <NewItemModal item={editItem} onClose={() => { setShowNewModal(false); setEditItem(null); }} onSaved={() => { setShowNewModal(false); setEditItem(null); loadItems(); }} />}

      {/* Product Builder (létrehozás + szerkesztés) */}
      {showBuilder && <ProductBuilderModal
        onClose={() => { setShowBuilder(false); setBuilderEditItem(null); }}
        onCreated={() => { setShowBuilder(false); setBuilderEditItem(null); loadItems(); }}
        editItem={builderEditItem ?? undefined}
      />}

      {/* Import wizard */}
      {showImport && <ImportWizard onClose={() => setShowImport(false)} onComplete={() => { setShowImport(false); loadItems(); }} />}
    </div>
  );
}

// ── Cikk modal (új + szerkesztés) ──

function NewItemModal({ item, onClose, onSaved }: { item?: Item | null; onClose: () => void; onSaved: () => void }) {
  const { toast } = useToast();
  const isEdit = !!item;
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'alapadatok' | 'változatok' | 'konfigurator' | 'szabályok' | 'kapcsolódó'>('alapadatok');
  const { activeFeatures } = useFeatureContext();
  const hasKonfigBasic = activeFeatures.includes('konfigurator_basic');
  const hasKonfigPro = activeFeatures.includes('konfigurator_pro');
  const initNavCode = item?.unitNavCode || 'PIECE';
  const [form, setForm] = useState({
    sku: item?.sku || '', name: item?.name || '', kind: item?.kind || 'product',
    unit: item?.unit || 'db', unitNavCode: initNavCode,
    netUnitPrice: String(item?.netUnitPrice || '0'),
    vatRate: item?.vatRate || '27', description: '',
    purchasePrice: item && (item as any).purchasePrice != null ? String((item as any).purchasePrice) : '',
  });
  const [imagePreview, setImagePreview] = useState<string | null>(item?.imageUrl || null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [compressModal, setCompressModal] = useState<{ file: File; objectUrl: string } | null>(null);

  async function handleSave() {
    setSaving(true);
    try {
      let savedId: string;
      const payload = {
        ...form,
        netUnitPrice: parseFloat(form.netUnitPrice || '0'),
        purchasePrice: form.purchasePrice !== '' ? parseFloat(form.purchasePrice) : null,
      };
      if (isEdit && item) {
        await patch(`/items/${item.id}`, payload);
        savedId = item.id;
      } else {
        const created = await post<any>('/items', payload);
        savedId = created.id;
      }
      // Upload image if selected
      if (imageFile && savedId) {
        setUploadingImage(true);
        const fd = new FormData();
        fd.append('image', imageFile);
        const token = localStorage.getItem('accessToken');
        const r = await fetch(`/api/items/${savedId}/image`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: fd,
        });
        if (!r.ok) toast('Kép feltöltése sikertelen.', 'error');
        setUploadingImage(false);
      }
      onSaved();
    } catch (e: any) { toast(e.message || 'Mentés sikertelen.', 'error'); } finally { setSaving(false); }
  }

  async function handleRemoveImage() {
    if (!item) return;
    try {
      await del(`/items/${item.id}/image`);
      setImagePreview(null);
      toast('Kép törölve.', 'success');
    } catch { toast('Törlés sikertelen.', 'error'); }
  }

  function handleImageChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = '';
    if (f.size > 5 * 1024 * 1024) {
      setCompressModal({ file: f, objectUrl: URL.createObjectURL(f) });
      return;
    }
    setImageFile(f);
    setImagePreview(URL.createObjectURL(f));
  }

  async function compressAndSet(file: File): Promise<void> {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0);
        // Binary search for quality that fits under 5 MB
        let lo = 0.1, hi = 0.95, best: Blob | null = null;
        const target = 5 * 1024 * 1024;
        const tryQuality = (q: number) => new Promise<Blob>(res => canvas.toBlob(b => res(b!), 'image/jpeg', q));
        (async () => {
          for (let i = 0; i < 8; i++) {
            const mid = (lo + hi) / 2;
            const blob = await tryQuality(mid);
            if (blob.size <= target) { best = blob; lo = mid; } else { hi = mid; }
          }
          if (!best) best = await tryQuality(0.1);
          const compressed = new File([best], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
          setImageFile(compressed);
          setImagePreview(URL.createObjectURL(compressed));
          resolve();
        })();
      };
      img.src = url;
    });
  }

  return (
    <>
    <div className="overlay show" style={{display:"flex",padding:"20px"}} onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-h">
          <h3>{isEdit ? 'Cikk szerkesztése' : '+ Új cikk'}</h3>
          <button onClick={onClose} className="x">&times;</button>
        </div>
        {isEdit && (
          <div style={{ display: 'flex', borderBottom: '1px solid var(--line)', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
            {(['alapadatok', ...(hasKonfigBasic ? ['változatok', 'konfigurator', 'szabályok', 'kapcsolódó'] : [])] as const).map(t => {
              const isLocked = (t === 'szabályok' || t === 'kapcsolódó') && !hasKonfigPro;
              return (
                <button key={t} onClick={() => !isLocked && setActiveTab(t as typeof activeTab)} style={{
                  padding: '9px 14px', border: 'none', background: 'none',
                  cursor: isLocked ? 'not-allowed' : 'pointer',
                  fontSize: 13, fontWeight: activeTab === t ? 700 : 500,
                  color: isLocked ? 'var(--line)' : activeTab === t ? 'var(--orange)' : 'var(--slate)',
                  borderBottom: activeTab === t ? '2px solid var(--orange)' : '2px solid transparent',
                  marginBottom: -1,
                  opacity: isLocked ? 0.5 : 1,
                }} title={isLocked ? 'Konfigurátor PRO addon szükséges' : undefined}>
                  {t === 'alapadatok' ? 'Alapadatok' : t === 'változatok' ? 'Változatok' : t === 'konfigurator' ? 'Konfigurátor' : t === 'szabályok' ? 'Szabályok 🔒' : 'Kapcsolódó 🔒'}
                </button>
              );
            })}
          </div>
        )}
        {activeTab === 'változatok' && item && (
          <VariantsTab itemId={item.id} />
        )}
        {activeTab === 'konfigurator' && item && (
          <KonfigTab itemId={item.id} />
        )}
        {activeTab === 'szabályok' && item && (
          <KonfigRulesTab itemId={item.id} />
        )}
        {activeTab === 'kapcsolódó' && item && (
          <RelatedTab itemId={item.id} />
        )}
        <div className="modal-b space-y-4" style={{ display: activeTab === 'alapadatok' ? undefined : 'none' }}>
          {/* Fotó */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '.05em', display: 'block', marginBottom: 8 }}>Termékfotó <span style={{ fontWeight: 400, textTransform: 'none' }}>(opcionális · JPEG, PNG, WebP · max 5 MB)</span></label>
            <div
              style={{ border: '2px dashed var(--line)', borderRadius: 'var(--r)', background: 'var(--mist)', minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', cursor: 'pointer', position: 'relative', transition: 'border-color .15s' }}
              onClick={() => document.getElementById('item-img-input')?.click()}
              onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--orange)'; }}
              onDragLeave={e => { e.currentTarget.style.borderColor = 'var(--line)'; }}
              onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--line)'; const f = e.dataTransfer.files[0]; if (f) { if (f.size > 5*1024*1024) return; setImageFile(f); setImagePreview(URL.createObjectURL(f)); } }}
            >
              <input id="item-img-input" type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={handleImageChange} />
              {imagePreview
                ? <img src={imagePreview} alt="preview" style={{ maxWidth: '100%', maxHeight: 280, objectFit: 'contain' }} />
                : <div style={{ textAlign: 'center', padding: 24, color: 'var(--slate)' }}>
                    <div style={{ fontSize: 40, marginBottom: 8 }}>📷</div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>Kattints vagy húzd ide a képet</div>
                    <div style={{ fontSize: 12, marginTop: 4, opacity: .7 }}>Az ajánlatkészítőben is megjelenik</div>
                  </div>
              }
            </div>
            {imagePreview && (
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <button onClick={e => { e.stopPropagation(); document.getElementById('item-img-input')?.click(); }} style={{ fontSize: 12, padding: '5px 12px', border: '1px solid var(--line)', borderRadius: 6, background: '#fff', cursor: 'pointer', color: 'var(--slate)', fontWeight: 600 }}>Csere</button>
                <button onClick={e => { e.stopPropagation(); if (isEdit) handleRemoveImage(); else { setImagePreview(null); setImageFile(null); }; }} style={{ fontSize: 12, padding: '5px 12px', border: '1px solid var(--line)', borderRadius: 6, background: '#fff', cursor: 'pointer', color: '#dc2626', fontWeight: 600 }}>Törlés</button>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', marginBottom: 12 }}>
                <div style={{ flex: 1 }}><Input label="SKU *" value={form.sku} onChange={e => setForm(f => ({ ...f, sku: e.target.value }))} placeholder="CIKK-001" /></div>
                <ScannerButton onScan={(code) => setForm(f => ({ ...f, sku: code }))} size="sm" />
              </div>
              <div>
                <label>Típus *</label>
                <select value={form.kind} onChange={e => setForm(f => ({ ...f, kind: e.target.value }))} className="form-input">
                  <option value="product">Termék</option>
                  <option value="service">Szolgáltatás</option>
                </select>
              </div>
            </div>
          </div>
          <Input label="Megnevezés *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Pl. Nyomtatópapír A4" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <MoneyInput label="Nettó egységár *" value={parseFloat(form.netUnitPrice || '0')} onChange={v => setForm(f => ({ ...f, netUnitPrice: String(v) }))} placeholder="0" />
            <div>
              <label>ÁFA-kulcs *</label>
              <select value={form.vatRate} onChange={e => setForm(f => ({ ...f, vatRate: e.target.value }))} className="form-input">
                <option value="27">27%</option><option value="18">18%</option><option value="5">5%</option><option value="0">0%</option>
              </select>
            </div>
            <div>
              <label>Egység *</label>
              <select
                value={form.unitNavCode}
                onChange={e => {
                  const nav = NAV_UNITS.find(u => u.code === e.target.value);
                  setForm(f => ({
                    ...f,
                    unitNavCode: e.target.value,
                    unit: nav && nav.code !== 'OWN' ? nav.abbr : (nav?.code === 'OWN' ? f.unit : nav?.abbr || f.unit),
                  }));
                }}
                className="form-input"
              >
                {NAV_UNITS.map(u => (
                  <option key={u.code} value={u.code}>
                    {u.code === 'OWN' ? 'Egyéb (saját)' : `${u.label} (${u.abbr})`}
                  </option>
                ))}
              </select>
              {form.unitNavCode === 'OWN' && (
                <input
                  value={form.unit}
                  onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
                  className="form-input"
                  style={{ marginTop: 6 }}
                  placeholder="Saját egység (pl. fm, pár, csomag…)"
                  maxLength={20}
                />
              )}
            </div>
          </div>
          <MoneyInput label="Beszerzési ár (opcionális)" value={form.purchasePrice !== '' ? parseFloat(form.purchasePrice) : 0} onChange={v => setForm(f => ({ ...f, purchasePrice: v === 0 ? '' : String(v) }))} placeholder="0" />
          <div>
            <label>Leírás</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="form-textarea" style={{height:"80px"}} placeholder="Opcionális leírás..." />
          </div>
        </div>
        {activeTab === 'alapadatok' && (
          <div style={{padding:"12px 20px",borderTop:"1px solid var(--line)",display:"flex",justifyContent:"flex-end",gap:"9px"}}>
            <Button variant="ghost" onClick={onClose}>Mégse</Button>
            <Button onClick={handleSave} loading={saving || uploadingImage} disabled={!form.sku || !form.name}>Mentés</Button>
          </div>
        )}
        {activeTab !== 'alapadatok' && (
          <div style={{padding:"12px 20px",borderTop:"1px solid var(--line)",display:"flex",justifyContent:"flex-end"}}>
            <Button variant="ghost" onClick={onClose}>Bezárás</Button>
          </div>
        )}
      </div>
    </div>

    {/* Tömörítés ajánlat modal */}
    {compressModal && (
      <div className="overlay show" style={{ display: 'flex', padding: 20, zIndex: 200 }} onClick={() => { URL.revokeObjectURL(compressModal.objectUrl); setCompressModal(null); }}>
        <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
          <div className="modal-h"><h3>A kép túl nagy</h3><button className="x" onClick={() => { URL.revokeObjectURL(compressModal.objectUrl); setCompressModal(null); }}>&times;</button></div>
          <div className="modal-b" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <img src={compressModal.objectUrl} alt="preview" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--line)', flexShrink: 0 }} />
              <div>
                <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 14 }}>{compressModal.file.name}</div>
                <div style={{ color: 'var(--slate)', fontSize: 13, marginTop: 4 }}>{(compressModal.file.size / (1024 * 1024)).toFixed(1)} MB — meghaladja az 5 MB-os limitet</div>
              </div>
            </div>
            <div style={{ background: 'var(--mist)', borderRadius: 'var(--r)', padding: '12px 14px', fontSize: 13, color: 'var(--slate)', lineHeight: 1.6 }}>
              Automatikusan tömörítjük a képet hogy belférjen 5 MB-ba. <strong>A felbontás és a kép tartalma nem változik</strong> — csak a JPEG minőség csökken minimálisan.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={() => { URL.revokeObjectURL(compressModal.objectUrl); setCompressModal(null); }}>Mégse</Button>
              <Button onClick={async () => {
                const f = compressModal.file;
                URL.revokeObjectURL(compressModal.objectUrl);
                setCompressModal(null);
                await compressAndSet(f);
              }}>Tömörítés és feltöltés</Button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

// ── Változatok tab ──

function VariantsTab({ itemId }: { itemId: string }) {
  const { toast } = useToast();
  const [variants, setVariants] = useState<Variant[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const emptyForm = { variantName: '', variantSuffix: '', variantSku: '', sortOrder: 0, netUnitPriceOverride: '', purchasePriceOverride: '' };
  const [form, setForm] = useState(emptyForm);

  async function load() {
    setLoading(true);
    try { setVariants(await get<Variant[]>(`/items/${itemId}/variants`)); } catch { /* */ }
    setLoading(false);
  }
  useEffect(() => { load(); }, [itemId]);

  async function handleAdd() {
    try {
      await post(`/items/${itemId}/variants`, {
        variantName: form.variantName.trim(),
        variantSuffix: form.variantSuffix.trim() || null,
        variantSku: form.variantSku.trim() || null,
        sortOrder: Number(form.sortOrder),
        netUnitPriceOverride: form.netUnitPriceOverride !== '' ? parseFloat(form.netUnitPriceOverride) : null,
        purchasePriceOverride: form.purchasePriceOverride !== '' ? parseFloat(form.purchasePriceOverride) : null,
      });
      toast('Változat hozzáadva.', 'success');
      setShowAdd(false); setForm(emptyForm); load();
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
  }

  async function handleEdit(v: Variant) {
    setEditId(v.id);
    setForm({
      variantName: v.variantName, variantSuffix: v.variantSuffix || '',
      variantSku: v.variantSku || '', sortOrder: v.sortOrder,
      netUnitPriceOverride: v.netUnitPriceOverride != null ? String(v.netUnitPriceOverride) : '',
      purchasePriceOverride: v.purchasePriceOverride != null ? String(v.purchasePriceOverride) : '',
    });
  }

  async function handleSaveEdit() {
    if (!editId) return;
    try {
      await patch(`/items/${itemId}/variants/${editId}`, {
        variantName: form.variantName.trim(),
        sortOrder: Number(form.sortOrder),
        netUnitPriceOverride: form.netUnitPriceOverride !== '' ? parseFloat(form.netUnitPriceOverride) : null,
        purchasePriceOverride: form.purchasePriceOverride !== '' ? parseFloat(form.purchasePriceOverride) : null,
      });
      toast('Mentve.', 'success'); setEditId(null); setForm(emptyForm); load();
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
  }

  async function handleDelete(variantId: string) {
    try {
      await del(`/items/${itemId}/variants/${variantId}`);
      toast('Változat törölve.', 'success'); load();
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
  }

  const inputStyle = { fontSize: 13, padding: '5px 8px', border: '1px solid var(--line)', borderRadius: 6, background: '#fff', width: '100%' };

  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 13, color: 'var(--slate)' }}>Termékváltozatok (pl. szín, méret — közös vagy egyedi SKU)</span>
        <Button size="sm" onClick={() => { setShowAdd(true); setEditId(null); setForm(emptyForm); }}>+ Változat</Button>
      </div>

      {showAdd && (
        <div style={{ background: 'var(--mist)', border: '1px solid var(--line)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 8, marginBottom: 8 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', display: 'block', marginBottom: 3 }}>Megnevezés *</label>
              <input style={inputStyle} value={form.variantName} onChange={e => setForm(f => ({ ...f, variantName: e.target.value }))} placeholder="pl. Antracit" />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', display: 'block', marginBottom: 3 }}>Suffix</label>
              <input style={inputStyle} value={form.variantSuffix} onChange={e => setForm(f => ({ ...f, variantSuffix: e.target.value }))} placeholder="/1" />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', display: 'block', marginBottom: 3 }}>Egyedi SKU (opc.)</label>
              <input style={inputStyle} value={form.variantSku} onChange={e => setForm(f => ({ ...f, variantSku: e.target.value }))} placeholder="Auto" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 8, marginBottom: 8 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', display: 'block', marginBottom: 3 }}>Nettó ár felülírás (Ft)</label>
              <input type="number" style={inputStyle} value={form.netUnitPriceOverride} onChange={e => setForm(f => ({ ...f, netUnitPriceOverride: e.target.value }))} placeholder="szülő ára" inputMode="decimal" />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', display: 'block', marginBottom: 3 }}>Besz. ár felülírás (Ft)</label>
              <input type="number" style={inputStyle} value={form.purchasePriceOverride} onChange={e => setForm(f => ({ ...f, purchasePriceOverride: e.target.value }))} placeholder="szülő ára" inputMode="decimal" />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', display: 'block', marginBottom: 3 }}>Sorrend</label>
              <input type="number" style={inputStyle} value={form.sortOrder} onChange={e => setForm(f => ({ ...f, sortOrder: Number(e.target.value) }))} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="ghost" size="sm" onClick={() => { setShowAdd(false); setForm(emptyForm); }}>Mégse</Button>
            <Button size="sm" onClick={handleAdd} disabled={!form.variantName.trim()}>Hozzáadás</Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-10 bg-gray-100 animate-pulse rounded" />)}</div>
      ) : variants.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--slate)', fontSize: 13 }}>Még nincs változat. Kattints a + Változat gombra.</div>
      ) : (
        <div style={{ border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden' }}>
          {variants.map((v, i) => editId === v.id ? (
            <div key={v.id} style={{ padding: 12, background: '#fffbeb', borderTop: i > 0 ? '1px solid var(--line)' : undefined }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px', gap: 8, marginBottom: 8 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', display: 'block', marginBottom: 3 }}>Megnevezés *</label>
                  <input style={inputStyle} value={form.variantName} onChange={e => setForm(f => ({ ...f, variantName: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', display: 'block', marginBottom: 3 }}>Sorrend</label>
                  <input type="number" style={inputStyle} value={form.sortOrder} onChange={e => setForm(f => ({ ...f, sortOrder: Number(e.target.value) }))} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', display: 'block', marginBottom: 3 }}>Nettó ár felülírás (Ft)</label>
                  <input type="number" style={inputStyle} value={form.netUnitPriceOverride} onChange={e => setForm(f => ({ ...f, netUnitPriceOverride: e.target.value }))} placeholder="szülő ára" inputMode="decimal" />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', display: 'block', marginBottom: 3 }}>Besz. ár felülírás (Ft)</label>
                  <input type="number" style={inputStyle} value={form.purchasePriceOverride} onChange={e => setForm(f => ({ ...f, purchasePriceOverride: e.target.value }))} placeholder="szülő ára" inputMode="decimal" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button variant="ghost" size="sm" onClick={() => { setEditId(null); setForm(emptyForm); }}>Mégse</Button>
                <Button size="sm" onClick={handleSaveEdit} disabled={!form.variantName.trim()}>Mentés</Button>
              </div>
            </div>
          ) : (
            <div key={v.id} style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', borderTop: i > 0 ? '1px solid var(--line)' : undefined, background: '#fff', gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--navy)' }}>{v.variantName}</div>
                <div style={{ fontSize: 11, color: 'var(--slate)', marginTop: 1 }}>
                  {v.variantSku && <span style={{ fontFamily: 'monospace' }}>{v.variantSku}</span>}
                  {v.netUnitPriceOverride != null && <span style={{ marginLeft: 8 }}>{Math.round(v.netUnitPriceOverride).toLocaleString('hu-HU')} Ft</span>}
                </div>
              </div>
              <button onClick={() => handleEdit(v)} style={{ fontSize: 12, padding: '4px 10px', border: '1px solid var(--line)', borderRadius: 6, background: '#fff', cursor: 'pointer', color: 'var(--slate)' }}>Szerkesztés</button>
              <button onClick={() => handleDelete(v.id)} style={{ fontSize: 12, padding: '4px 10px', border: '1px solid var(--line)', borderRadius: 6, background: '#fff', cursor: 'pointer', color: '#dc2626' }}>Törlés</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Konfigurátor input form (module-szintű — ne definiálj komponenst render belsejében!) ──

type KonfigInputFormState = {
  kod: string; nev: string; tipus: 'LISTA' | 'SZAM' | 'SZOVEG';
  opciokStr: string; egyseg: string; minErtek: string; maxErtek: string; sortOrder: number;
};

const konfigInputStyle = { fontSize: 13, padding: '5px 8px', border: '1px solid var(--line)', borderRadius: 6, background: '#fff', width: '100%' } as const;

function KonfigInputForm({ form, setForm, editId, onSave, onCancel }: {
  form: KonfigInputFormState;
  setForm: Dispatch<SetStateAction<KonfigInputFormState>>;
  editId: string | null;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div style={{ background: 'var(--mist)', border: '1px solid var(--line)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8, marginBottom: 8 }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', display: 'block', marginBottom: 3 }}>Kód * <span style={{ fontWeight: 400 }}>(képletben)</span></label>
          <input style={konfigInputStyle} value={form.kod} onChange={e => setForm(f => ({ ...f, kod: e.target.value }))} placeholder="pl. szin, szel" readOnly={!!editId} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', display: 'block', marginBottom: 3 }}>Megnevezés *</label>
          <input style={konfigInputStyle} value={form.nev} onChange={e => setForm(f => ({ ...f, nev: e.target.value }))} placeholder="pl. Szín" />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', display: 'block', marginBottom: 3 }}>Típus *</label>
          <select style={konfigInputStyle} value={form.tipus} onChange={e => setForm(f => ({ ...f, tipus: e.target.value as 'LISTA' | 'SZAM' | 'SZOVEG' }))}>
            <option value="LISTA">Lista</option>
            <option value="SZAM">Szám</option>
            <option value="SZOVEG">Szöveg</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', display: 'block', marginBottom: 3 }}>Sorrend</label>
          <input type="number" style={konfigInputStyle} value={form.sortOrder} onChange={e => setForm(f => ({ ...f, sortOrder: Number(e.target.value) }))} />
        </div>
      </div>
      {form.tipus === 'LISTA' && (
        <div style={{ marginBottom: 8 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', display: 'block', marginBottom: 3 }}>Opciók (soronként egy)</label>
          <textarea style={{ ...konfigInputStyle, height: 72, resize: 'vertical' }} value={form.opciokStr} onChange={e => setForm(f => ({ ...f, opciokStr: e.target.value }))} placeholder={'antracit\nfehér\nsötétbarna'} />
        </div>
      )}
      {form.tipus === 'SZAM' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8, marginBottom: 8 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', display: 'block', marginBottom: 3 }}>Egység (pl. cm)</label>
            <input style={konfigInputStyle} value={form.egyseg} onChange={e => setForm(f => ({ ...f, egyseg: e.target.value }))} placeholder="cm" />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', display: 'block', marginBottom: 3 }}>Min. érték</label>
            <input type="number" style={konfigInputStyle} value={form.minErtek} onChange={e => setForm(f => ({ ...f, minErtek: e.target.value }))} placeholder="0" inputMode="decimal" />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', display: 'block', marginBottom: 3 }}>Max. érték</label>
            <input type="number" style={konfigInputStyle} value={form.maxErtek} onChange={e => setForm(f => ({ ...f, maxErtek: e.target.value }))} placeholder="∞" inputMode="decimal" />
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button variant="ghost" size="sm" onClick={onCancel}>Mégse</Button>
        <Button size="sm" onClick={onSave} disabled={!form.kod.trim() || !form.nev.trim()}>
          {editId ? 'Mentés' : 'Hozzáadás'}
        </Button>
      </div>
    </div>
  );
}

// ── Konfigurátor inputok tab ──

function KonfigTab({ itemId }: { itemId: string }) {
  const { toast } = useToast();
  const [inputs, setInputs] = useState<KonfigInput[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const emptyForm: KonfigInputFormState = { kod: '', nev: '', tipus: 'LISTA', opciokStr: '', egyseg: '', minErtek: '', maxErtek: '', sortOrder: 0 };
  const [form, setForm] = useState<KonfigInputFormState>(emptyForm);

  async function load() {
    setLoading(true);
    try { setInputs(await get<KonfigInput[]>(`/items/${itemId}/konfig-inputs`)); } catch { /* */ }
    setLoading(false);
  }
  useEffect(() => { load(); }, [itemId]);

  function buildPayload() {
    return {
      kod: form.kod.trim(),
      nev: form.nev.trim(),
      tipus: form.tipus,
      opciok: form.tipus === 'LISTA' && form.opciokStr.trim()
        ? form.opciokStr.split('\n').map(s => s.trim()).filter(Boolean)
        : null,
      egyseg: form.tipus === 'SZAM' && form.egyseg.trim() ? form.egyseg.trim() : null,
      minErtek: form.tipus === 'SZAM' && form.minErtek !== '' ? parseFloat(form.minErtek) : null,
      maxErtek: form.tipus === 'SZAM' && form.maxErtek !== '' ? parseFloat(form.maxErtek) : null,
      sortOrder: Number(form.sortOrder),
    };
  }

  async function handleAdd() {
    try {
      await post(`/items/${itemId}/konfig-inputs`, buildPayload());
      toast('Input hozzáadva.', 'success');
      setShowAdd(false); setForm(emptyForm); load();
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
  }

  async function handleSaveEdit() {
    if (!editId) return;
    try {
      await patch(`/items/${itemId}/konfig-inputs/${editId}`, buildPayload());
      toast('Mentve.', 'success'); setEditId(null); setForm(emptyForm); load();
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
  }

  async function handleDelete(inputId: string) {
    try {
      await del(`/items/${itemId}/konfig-inputs/${inputId}`);
      toast('Input törölve.', 'success'); load();
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
  }

  function startEdit(inp: KonfigInput) {
    setEditId(inp.id);
    setForm({
      kod: inp.kod, nev: inp.nev, tipus: inp.tipus,
      opciokStr: inp.opciok ? inp.opciok.join('\n') : '',
      egyseg: inp.egyseg || '', minErtek: inp.minErtek != null ? String(inp.minErtek) : '',
      maxErtek: inp.maxErtek != null ? String(inp.maxErtek) : '',
      sortOrder: inp.sortOrder,
    });
  }

  const tipusBadge = (t: string) => t === 'LISTA' ? { background: '#eff6ff', color: '#1d4ed8' } : t === 'SZAM' ? { background: '#f0fdf4', color: '#15803d' } : { background: '#faf5ff', color: '#7e22ce' };

  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 13, color: 'var(--slate)' }}>Konfigurátor inputok — a megrendelés felvételénél megjelenő mezők</span>
        <Button size="sm" onClick={() => { setShowAdd(true); setEditId(null); setForm(emptyForm); }}>+ Input</Button>
      </div>

      {showAdd && !editId && (
        <KonfigInputForm form={form} setForm={setForm} editId={editId} onSave={handleAdd} onCancel={() => { setShowAdd(false); setForm(emptyForm); }} />
      )}

      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-10 bg-gray-100 animate-pulse rounded" />)}</div>
      ) : inputs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--slate)', fontSize: 13 }}>Még nincs konfigurátor input. Kattints a + Input gombra.</div>
      ) : (
        <div style={{ border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden' }}>
          {inputs.map((inp, i) => editId === inp.id ? (
            <div key={inp.id} style={{ padding: 12, background: '#fffbeb', borderTop: i > 0 ? '1px solid var(--line)' : undefined }}>
              <KonfigInputForm form={form} setForm={setForm} editId={editId} onSave={handleSaveEdit} onCancel={() => { setEditId(null); setForm(emptyForm); }} />
            </div>
          ) : (
            <div key={inp.id} style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', borderTop: i > 0 ? '1px solid var(--line)' : undefined, background: '#fff', gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)', fontFamily: 'monospace' }}>{inp.kod}</span>
                  <span style={{ ...tipusBadge(inp.tipus), fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10 }}>{inp.tipus}</span>
                  <span style={{ fontSize: 13, color: 'var(--slate)' }}>{inp.nev}</span>
                </div>
                {inp.opciok && inp.opciok.length > 0 && (
                  <div style={{ fontSize: 11, color: 'var(--slate)', marginTop: 2 }}>{inp.opciok.join(', ')}</div>
                )}
                {inp.egyseg && (
                  <div style={{ fontSize: 11, color: 'var(--slate)', marginTop: 2 }}>
                    Egység: {inp.egyseg}
                    {inp.minErtek != null && ` · min: ${inp.minErtek}`}
                    {inp.maxErtek != null && ` · max: ${inp.maxErtek}`}
                  </div>
                )}
              </div>
              <button onClick={() => { setShowAdd(false); startEdit(inp); }} style={{ fontSize: 12, padding: '4px 10px', border: '1px solid var(--line)', borderRadius: 6, background: '#fff', cursor: 'pointer', color: 'var(--slate)' }}>Szerkesztés</button>
              <button onClick={() => handleDelete(inp.id)} style={{ fontSize: 12, padding: '4px 10px', border: '1px solid var(--line)', borderRadius: 6, background: '#fff', cursor: 'pointer', color: '#dc2626' }}>Törlés</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Szabályok tab ──

type RuleType = 'VALUE_PRICE' | 'SIZE_FORMULA' | 'BASE_PRICE_FORMULA' | 'CONDITIONAL_ITEM' | 'CART_SLAB' | 'SIZE_SLAB';

interface KonfigRule {
  id: string; itemId: string; ruleType: RuleType;
  ruleParams: Record<string, unknown>; sortOrder: number;
}

interface EvalResult {
  basePrice: number; costPrice: number; profit: number; m2OrQty: number;
  addedItems: { itemSku: string; qty: number }[];
  vars: Record<string, number>; errors: string[];
}

const RULE_TYPE_LABELS: Record<RuleType, string> = {
  VALUE_PRICE: 'Érték-alapú ár',
  SIZE_FORMULA: 'Méret-képlet',
  BASE_PRICE_FORMULA: 'Alapár-képlet',
  CONDITIONAL_ITEM: 'Feltételes tétel',
  CART_SLAB: 'Kosárérték-sáv',
  SIZE_SLAB: 'Méret/mennyiség-sáv',
};

function KonfigRulesTab({ itemId }: { itemId: string }) {
  const { toast } = useToast();
  const [rules, setRules] = useState<KonfigRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editRule, setEditRule] = useState<KonfigRule | null>(null);
  const [selectedType, setSelectedType] = useState<RuleType>('VALUE_PRICE');
  const [paramsJson, setParamsJson] = useState('{}');
  const [jsonError, setJsonError] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [formulaMode, setFormulaMode] = useState(false);
  // Test panel
  const [testInputs, setTestInputs] = useState('{}');
  const [testResult, setTestResult] = useState<EvalResult | null>(null);
  const [testing, setTesting] = useState(false);

  async function load() {
    setLoading(true);
    try { setRules(await get<KonfigRule[]>(`/items/${itemId}/rules`)); } catch { /* */ }
    setLoading(false);
  }
  useEffect(() => { load(); }, [itemId]);

  function validateJson(s: string) {
    try { JSON.parse(s); setJsonError(''); return true; } catch (e: any) { setJsonError(e.message); return false; }
  }

  async function handleAdd() {
    if (!validateJson(paramsJson)) return;
    try {
      await post(`/items/${itemId}/rules`, { ruleType: selectedType, ruleParams: JSON.parse(paramsJson), sortOrder });
      toast('Szabály hozzáadva.', 'success');
      setShowAdd(false); setParamsJson('{}'); setSortOrder(0); load();
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
  }

  async function handleSaveEdit() {
    if (!editRule || !validateJson(paramsJson)) return;
    try {
      await patch(`/items/${itemId}/rules/${editRule.id}`, { ruleParams: JSON.parse(paramsJson), sortOrder });
      toast('Mentve.', 'success'); setEditRule(null); setParamsJson('{}'); load();
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
  }

  async function handleDelete(ruleId: string) {
    try { await del(`/items/${itemId}/rules/${ruleId}`); toast('Szabály törölve.', 'success'); load(); }
    catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
  }

  async function handleTest() {
    let inp: Record<string, unknown>;
    try { inp = JSON.parse(testInputs); } catch { toast('Érvénytelen JSON az inputoknál.', 'error'); return; }
    setTesting(true);
    try {
      const r = await post<EvalResult>(`/items/${itemId}/evaluate`, { inputs: inp });
      setTestResult(r);
    } catch (e: any) { toast(e.message || 'Kiértékelési hiba.', 'error'); }
    setTesting(false);
  }

  function startEdit(r: KonfigRule) {
    setEditRule(r); setSelectedType(r.ruleType);
    setParamsJson(JSON.stringify(r.ruleParams, null, 2));
    setSortOrder(r.sortOrder); setShowAdd(false);
  }

  // Template JSON az adott típushoz
  function templateFor(t: RuleType): string {
    const templates: Record<RuleType, object> = {
      VALUE_PRICE: { inputKod: 'szin', values: { antracit: 33000, feher: 30000 }, defaultPrice: 30000 },
      SIZE_FORMULA: { expression: 'szel*mag/10000', minValue: 1.3, resultVar: 'm2' },
      BASE_PRICE_FORMULA: { expression: 'egysegar*m2', costExpression: 'besz*m2' },
      CONDITIONAL_ITEM: { conditions: [{ inputKod: 'kezeles', inputValue: 'motoros', thresholdVar: 'm2', operator: '<', threshold: 4, itemSku: 'S10', qty: 1 }] },
      CART_SLAB: { slabs: [{ upTo: 5000, price: 3000 }, { upTo: 50000, price: 2000 }, { upTo: null, price: 0 }] },
      SIZE_SLAB: { inputKod: 'anyag', slabs: [{ inputValue: 'vekony', rows: [{ upTo: 5, price: 300 }, { upTo: null, price: 250 }] }] },
    };
    return JSON.stringify(templates[t], null, 2);
  }

  const inputStyle = { fontSize: 13, padding: '5px 8px', border: '1px solid var(--line)', borderRadius: 6, background: '#fff', width: '100%' };

  return (
    <div style={{ padding: '16px 20px', maxHeight: 520, overflowY: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 13, color: 'var(--slate)' }}>Árazási és tétel-hozzáadási szabályok (a motor sorban hajtja végre)</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <Button variant="ghost" size="sm" onClick={() => setFormulaMode(f => !f)}>{formulaMode ? 'Sablon-mód' : 'Haladó JSON-mód'}</Button>
          <Button size="sm" onClick={() => { setShowAdd(true); setEditRule(null); setParamsJson(templateFor(selectedType)); setJsonError(''); }}>+ Szabály</Button>
        </div>
      </div>

      {(showAdd || editRule) && (
        <div style={{ background: 'var(--mist)', border: '1px solid var(--line)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 70px', gap: 8, marginBottom: 8 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', display: 'block', marginBottom: 3 }}>Típus</label>
              <select style={inputStyle} value={selectedType} onChange={e => { setSelectedType(e.target.value as RuleType); if (!editRule) setParamsJson(templateFor(e.target.value as RuleType)); }} disabled={!!editRule}>
                {(Object.keys(RULE_TYPE_LABELS) as RuleType[]).map(t => (
                  <option key={t} value={t}>{RULE_TYPE_LABELS[t]}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', display: 'block', marginBottom: 3 }}>Sorrend</label>
              <input type="number" style={inputStyle} value={sortOrder} onChange={e => setSortOrder(Number(e.target.value))} />
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', display: 'block', marginBottom: 3 }}>
              Paraméterek (JSON) — {RULE_TYPE_LABELS[selectedType]}
            </label>
            <textarea
              style={{ ...inputStyle, height: 140, fontFamily: 'monospace', fontSize: 12, resize: 'vertical' }}
              value={paramsJson}
              onChange={e => { setParamsJson(e.target.value); validateJson(e.target.value); }}
            />
            {jsonError && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>{jsonError}</div>}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <Button variant="ghost" size="sm" onClick={() => { setShowAdd(false); setEditRule(null); setParamsJson('{}'); }}>Mégse</Button>
            <Button size="sm" onClick={editRule ? handleSaveEdit : handleAdd} disabled={!!jsonError}>
              {editRule ? 'Mentés' : 'Hozzáadás'}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-10 bg-gray-100 animate-pulse rounded" />)}</div>
      ) : rules.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--slate)', fontSize: 13 }}>Még nincs szabály. Kattints a + Szabály gombra.</div>
      ) : (
        <div style={{ border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
          {rules.map((r, i) => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'flex-start', padding: '10px 12px', borderTop: i > 0 ? '1px solid var(--line)' : undefined, background: '#fff', gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 2 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, background: '#eff6ff', color: '#1d4ed8', padding: '2px 7px', borderRadius: 10 }}>{RULE_TYPE_LABELS[r.ruleType]}</span>
                  <span style={{ fontSize: 11, color: 'var(--slate)' }}>#{r.sortOrder}</span>
                </div>
                <code style={{ fontSize: 11, color: 'var(--slate)', wordBreak: 'break-all' }}>
                  {JSON.stringify(r.ruleParams).slice(0, 120)}{JSON.stringify(r.ruleParams).length > 120 ? '…' : ''}
                </code>
              </div>
              <button onClick={() => startEdit(r)} style={{ fontSize: 12, padding: '4px 10px', border: '1px solid var(--line)', borderRadius: 6, background: '#fff', cursor: 'pointer', color: 'var(--slate)', flexShrink: 0 }}>Szerk.</button>
              <button onClick={() => handleDelete(r.id)} style={{ fontSize: 12, padding: '4px 10px', border: '1px solid var(--line)', borderRadius: 6, background: '#fff', cursor: 'pointer', color: '#dc2626', flexShrink: 0 }}>Törlés</button>
            </div>
          ))}
        </div>
      )}

      {/* Teszt panel */}
      <div style={{ border: '1px solid var(--line)', borderRadius: 8, padding: 12, background: '#f8faff' }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)', marginBottom: 8 }}>Teszt / Előnézet panel</div>
        <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', display: 'block', marginBottom: 3 }}>Minta-inputok (JSON) — pl. {"{"}"szin":"antracit","szel":320,"mag":150,"kezeles":"motoros"{"}"}</label>
        <textarea
          style={{ ...inputStyle, height: 64, fontFamily: 'monospace', fontSize: 12, resize: 'vertical', marginBottom: 8 }}
          value={testInputs}
          onChange={e => setTestInputs(e.target.value)}
          placeholder='{"szin":"antracit","szel":320,"mag":150}'
        />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <Button size="sm" onClick={handleTest} loading={testing}>Kiértékelés</Button>
        </div>
        {testResult && (
          <div style={{ background: '#fff', border: '1px solid var(--line)', borderRadius: 6, padding: 10, fontSize: 13 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--slate)', marginBottom: 2 }}>Eladási ár</div>
                <div style={{ fontWeight: 700, color: 'var(--navy)' }}>{Math.round(testResult.basePrice).toLocaleString('hu-HU')} Ft</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--slate)', marginBottom: 2 }}>Önköltség</div>
                <div style={{ fontWeight: 700, color: 'var(--slate)' }}>{Math.round(testResult.costPrice).toLocaleString('hu-HU')} Ft</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--slate)', marginBottom: 2 }}>Haszon</div>
                <div style={{ fontWeight: 700, color: testResult.profit >= 0 ? '#15803d' : '#dc2626' }}>{Math.round(testResult.profit).toLocaleString('hu-HU')} Ft</div>
              </div>
            </div>
            {testResult.m2OrQty !== 1 && (
              <div style={{ fontSize: 12, color: 'var(--slate)', marginBottom: 4 }}>Számított m²/mennyiség: <strong>{testResult.m2OrQty.toFixed(4)}</strong></div>
            )}
            {Object.keys(testResult.vars).length > 0 && (
              <div style={{ fontSize: 11, color: 'var(--slate)', marginBottom: 4 }}>Változók: {Object.entries(testResult.vars).map(([k,v]) => `${k}=${v}`).join(', ')}</div>
            )}
            {testResult.addedItems.length > 0 && (
              <div style={{ fontSize: 12, color: 'var(--navy)', marginBottom: 4 }}>
                Hozzáadott tételek: {testResult.addedItems.map(ai => `${ai.itemSku} × ${ai.qty}`).join(', ')}
              </div>
            )}
            {testResult.errors.length > 0 && (
              <div style={{ fontSize: 11, color: '#dc2626' }}>Hibák: {testResult.errors.join('; ')}</div>
            )}
          </div>
        )}
      </div>

      {/* Bezárás */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
        {/* handled by outer modal footer — just need spacing */}
      </div>
    </div>
  );
}

// ── Kapcsolódó termékek tab ──

interface RelatedItem {
  id: string; parentItemId: string; relatedItemId: string;
  relatedName: string; relatedSku: string;
  relationType: 'REQUIRED' | 'SUGGESTED';
  variantMatched: boolean; qtyFixed: number | null; qtyFormula: string | null;
}

function RelatedTab({ itemId }: { itemId: string }) {
  const { toast } = useToast();
  const [related, setRelated] = useState<RelatedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ relatedItemId: '', relationType: 'REQUIRED' as 'REQUIRED'|'SUGGESTED', variantMatched: false, qtyFixed: '', qtyFormula: '' });
  const [itemSearch, setItemSearch] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);

  async function load() {
    setLoading(true);
    try { setRelated(await get<RelatedItem[]>(`/items/${itemId}/related`)); } catch { /* */ }
    setLoading(false);
  }
  useEffect(() => { load(); }, [itemId]);

  useEffect(() => {
    if (!itemSearch.trim()) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await get<any>(`/items?search=${encodeURIComponent(itemSearch)}&pageSize=10`);
        setSearchResults(Array.isArray(r) ? r : r?.data || []);
      } catch { setSearchResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [itemSearch]);

  async function handleAdd() {
    if (!form.relatedItemId) { toast('Válassz kapcsolódó cikket.', 'error'); return; }
    try {
      await post(`/items/${itemId}/related`, {
        relatedItemId: form.relatedItemId,
        relationType: form.relationType,
        variantMatched: form.variantMatched,
        qtyFixed: form.qtyFixed !== '' ? parseFloat(form.qtyFixed) : null,
        qtyFormula: form.qtyFormula.trim() || null,
      });
      toast('Kapcsolódó termék hozzáadva.', 'success');
      setShowAdd(false); setForm({ relatedItemId: '', relationType: 'REQUIRED', variantMatched: false, qtyFixed: '', qtyFormula: '' }); setItemSearch(''); setSearchResults([]); load();
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
  }

  async function handleDelete(relId: string) {
    try { await del(`/items/${itemId}/related/${relId}`); toast('Törölve.', 'success'); load(); }
    catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
  }

  const inputStyle = { fontSize: 13, padding: '5px 8px', border: '1px solid var(--line)', borderRadius: 6, background: '#fff', width: '100%' };

  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 13, color: 'var(--slate)' }}>Kötelező (auto) és javasolt (opt-in) kapcsolódó termékek</span>
        <Button size="sm" onClick={() => setShowAdd(true)}>+ Kapcsolódó</Button>
      </div>

      {showAdd && (
        <div style={{ background: 'var(--mist)', border: '1px solid var(--line)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
          <div style={{ marginBottom: 8 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', display: 'block', marginBottom: 3 }}>Cikk keresése *</label>
            <input style={inputStyle} value={itemSearch} onChange={e => setItemSearch(e.target.value)} placeholder="SKU / megnevezés..." />
            {searchResults.length > 0 && (
              <div style={{ border: '1px solid var(--line)', borderRadius: 6, background: '#fff', marginTop: 4, maxHeight: 140, overflowY: 'auto' }}>
                {searchResults.filter(r => r.id !== itemId).map((r: any) => (
                  <button key={r.id} type="button" onClick={() => { setForm(f => ({ ...f, relatedItemId: r.id })); setItemSearch(r.name); setSearchResults([]); }}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 12 }}>
                    <strong>{r.sku}</strong> — {r.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', display: 'block', marginBottom: 3 }}>Típus</label>
              <select style={inputStyle} value={form.relationType} onChange={e => setForm(f => ({ ...f, relationType: e.target.value as any }))}>
                <option value="REQUIRED">Kötelező</option>
                <option value="SUGGESTED">Javasolt</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', display: 'block', marginBottom: 3 }}>Fix mennyiség</label>
              <input type="number" style={inputStyle} value={form.qtyFixed} onChange={e => setForm(f => ({ ...f, qtyFixed: e.target.value }))} placeholder="1" inputMode="decimal" />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', display: 'block', marginBottom: 3 }}>Mennyiség-képlet</label>
              <input style={inputStyle} value={form.qtyFormula} onChange={e => setForm(f => ({ ...f, qtyFormula: e.target.value }))} placeholder="pl. m2" />
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.variantMatched} onChange={e => setForm(f => ({ ...f, variantMatched: e.target.checked }))} />
            Variáns-egyeztetett (a szülő suffix-e alapján)
          </label>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="ghost" size="sm" onClick={() => { setShowAdd(false); setItemSearch(''); setSearchResults([]); }}>Mégse</Button>
            <Button size="sm" onClick={handleAdd} disabled={!form.relatedItemId}>Hozzáadás</Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">{[1,2].map(i => <div key={i} className="h-10 bg-gray-100 animate-pulse rounded" />)}</div>
      ) : related.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--slate)', fontSize: 13 }}>Még nincs kapcsolódó termék.</div>
      ) : (
        <div style={{ border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden' }}>
          {related.map((r, i) => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', borderTop: i > 0 ? '1px solid var(--line)' : undefined, background: '#fff', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: r.relationType === 'REQUIRED' ? '#fef2f2' : '#f0fdf4', color: r.relationType === 'REQUIRED' ? '#dc2626' : '#15803d' }}>{r.relationType === 'REQUIRED' ? 'Kötelező' : 'Javasolt'}</span>
                  <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: 'var(--navy)' }}>{r.relatedSku}</span>
                  <span style={{ fontSize: 13, color: 'var(--slate)' }}>{r.relatedName}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--slate)', marginTop: 2 }}>
                  {r.variantMatched && '• Variáns-egyeztetett '}
                  {r.qtyFixed != null && `• Fix: ${r.qtyFixed}`}
                  {r.qtyFormula && `• Képlet: ${r.qtyFormula}`}
                </div>
              </div>
              <button onClick={() => handleDelete(r.id)} style={{ fontSize: 12, padding: '4px 10px', border: '1px solid var(--line)', borderRadius: 6, background: '#fff', cursor: 'pointer', color: '#dc2626' }}>Törlés</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Árlisták tab ──

interface PriceList {
  id: string; name: string; is_default: boolean; currency: string;
  is_archived: boolean; item_count: number | string;
}

function PriceListsTab({ hasMultiplePriceLists }: { hasMultiplePriceLists: boolean }) {
  const { toast } = useToast();
  const [lists, setLists] = useState<PriceList[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState<PriceList | null>(null);

  async function load() {
    setLoading(true);
    try { setLists(await get<PriceList[]>('/price-lists')); } catch { /* */ }
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  async function createList() {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await post('/price-lists', { name: newName.trim(), currency: 'HUF', isDefault: false });
      toast('Árlista létrehozva.', 'success');
      setShowNew(false); setNewName(''); load();
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
    setSaving(false);
  }

  async function archiveList(id: string) {
    try { await post(`/price-lists/${id}/archive`, {}); toast('Archiválva.', 'success'); load(); }
    catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
  }

  async function setDefault(id: string) {
    try { await post(`/price-lists/${id}/set-default`, {}); load(); }
    catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
  }

  if (selected) return <PriceListDetail list={selected} onBack={() => { setSelected(null); load(); }} />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 13, color: 'var(--slate)' }}>
          {hasMultiplePriceLists
            ? 'Több árlista elérhető. Partnerenként állíthatsz be eltérő árakat.'
            : <span>Jelenleg 1 árlista érhető el. <strong>Standard</strong> csomaggal több is lehetséges.</span>}
        </div>
        <Button size="sm" onClick={() => {
          if (!hasMultiplePriceLists && lists.length >= 1) {
            toast('Több árlista Standard vagy magasabb csomagban érhető el.', 'error'); return;
          }
          setShowNew(true);
        }}>+ Új árlista</Button>
      </div>

      {loading ? (
        <div className="space-y-2">{[1,2].map(i => <div key={i} className="h-14 bg-gray-100 animate-pulse rounded" />)}</div>
      ) : lists.length === 0 ? (
        <EmptyState icon="💰" title="Nincs árlista" description="Hozd létre az első árlistát." action={<Button size="sm" onClick={() => setShowNew(true)}>+ Új árlista</Button>} />
      ) : (
        <div className="bg-white rounded-xl border border-[var(--color-border)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <th className="px-4 py-3">Név</th>
                <th className="px-4 py-3">Deviza</th>
                <th className="px-4 py-3">Cikkek</th>
                <th className="px-4 py-3">Alap</th>
                <th className="px-4 py-3">Műveletek</th>
              </tr>
            </thead>
            <tbody>
              {lists.map(pl => (
                <tr key={pl.id} className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => setSelected(pl)}>
                  <td className="px-4 py-3 font-medium text-[#1a2540]">{pl.name}</td>
                  <td className="px-4 py-3 text-gray-600">{pl.currency}</td>
                  <td className="px-4 py-3 text-gray-600">{pl.item_count}</td>
                  <td className="px-4 py-3">
                    {pl.is_default ? <Badge variant="success">Alapértelmezett</Badge> : null}
                  </td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <ActionsDropdown actions={[
                      { label: 'Tételek kezelése', icon: '📋', onClick: () => setSelected(pl) },
                      ...(!pl.is_default ? [{ label: 'Beállítás alapértelmezettnek', icon: '⭐', onClick: () => setDefault(pl.id) }] : []),
                      { separator: true, label: '', onClick: () => {} },
                      ...(!pl.is_default ? [{ label: 'Archiválás', icon: '🗄', onClick: () => archiveList(pl.id), danger: true }] : []),
                    ]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNew && (
        <div className="overlay show" style={{ display: 'flex', padding: 20 }} onClick={() => setShowNew(false)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-h"><h3>Új árlista</h3><button className="x" onClick={() => setShowNew(false)}>&times;</button></div>
            <div className="modal-b space-y-3">
              <Input label="Árlista neve *" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Pl. Nagykereskedelmi árlista" autoFocus />
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--line)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="ghost" onClick={() => setShowNew(false)}>Mégse</Button>
              <Button onClick={createList} loading={saving} disabled={!newName.trim()}>Létrehozás</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface FullItem {
  item_id: string; item_name: string; sku: string;
  base_price: number; fixed_net_price: number | null;
}

const PAGE_SIZE = 50;

function PriceListDetail({ list, onBack }: { list: PriceList; onBack: () => void }) {
  const { toast } = useToast();
  const [rows, setRows] = useState<FullItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // edits: item_id → string (empty = alapár, szám = override)
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [originals, setOriginals] = useState<Record<string, number | null>>({});
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [bulkPercent, setBulkPercent] = useState('');

  const dirty = Object.entries(edits).some(([id, val]) => {
    const orig = originals[id] ?? null;
    const parsed = val === '' ? null : parseFloat(val);
    return parsed !== orig;
  });

  async function load(p: number, s: string) {
    setLoading(true);
    try {
      const q = new URLSearchParams({ page: String(p), pageSize: String(PAGE_SIZE) });
      if (s) q.set('search', s);
      const data = await get<any>(`/price-lists/${list.id}/items/full?${q}`);
      const items: FullItem[] = data.data || [];
      setRows(items);
      setTotal(data.total || 0);
      // Update originals & edits for newly loaded rows (don't overwrite user edits)
      setOriginals(prev => {
        const next = { ...prev };
        items.forEach(r => { next[r.item_id] = r.fixed_net_price; });
        return next;
      });
      setEdits(prev => {
        const next = { ...prev };
        items.forEach(r => {
          if (!(r.item_id in next)) {
            next[r.item_id] = r.fixed_net_price != null ? String(r.fixed_net_price) : '';
          }
        });
        return next;
      });
    } catch { /* */ }
    setLoading(false);
  }

  useEffect(() => { load(page, search); }, []);

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(1, search); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  function goPage(p: number) { setPage(p); load(p, search); }

  function setEdit(itemId: string, val: string) {
    setEdits(prev => ({ ...prev, [itemId]: val }));
  }

  function clearEdit(itemId: string) {
    setEdits(prev => ({ ...prev, [itemId]: '' }));
  }

  function applyBulkPercent() {
    const pct = parseFloat(bulkPercent);
    if (isNaN(pct)) return;
    setEdits(prev => {
      const next = { ...prev };
      rows.forEach(r => {
        const newPrice = Math.round(r.base_price * (1 + pct / 100));
        next[r.item_id] = String(newPrice);
      });
      return next;
    });
    setBulkPercent('');
  }

  async function handleSave() {
    setSaving(true);
    try {
      const items = Object.entries(edits)
        .filter(([id, val]) => {
          const orig = originals[id] ?? null;
          const parsed = val === '' ? null : parseFloat(val);
          return parsed !== orig;
        })
        .map(([itemId, val]) => ({
          itemId,
          fixedNetPrice: val === '' ? null : parseFloat(val),
        }));
      if (items.length === 0) { toast('Nincs változás.', 'success'); setSaving(false); return; }
      await post(`/price-lists/${list.id}/items/batch`, { items });
      // Refresh originals
      const newOrig = { ...originals };
      items.forEach(({ itemId, fixedNetPrice }) => { newOrig[itemId] = fixedNetPrice; });
      setOriginals(newOrig);
      toast(`${items.length} sor mentve.`, 'success');
    } catch (e: any) { toast(e.message || 'Mentés sikertelen.', 'error'); }
    setSaving(false);
  }

  function handleBack() {
    if (dirty) { setShowLeaveModal(true); } else { onBack(); }
  }

  function formatP(v: number | null | undefined) {
    if (v == null) return '—';
    return Math.round(v).toLocaleString('hu-HU') + ' Ft';
  }

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={handleBack} style={{ fontSize: 13, color: 'var(--orange)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>← Vissza</button>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--navy)' }}>{list.name}</h3>
        {list.is_default && <Badge variant="success">Alapértelmezett</Badge>}
        {dirty && <Badge variant="warning">Nem mentett változások</Badge>}
        <div style={{ marginLeft: 'auto' }}>
          <Button size="sm" onClick={handleSave} loading={saving} disabled={!dirty}>Mentés</Button>
        </div>
      </div>

      {/* Toolbar: keresés + tömeges kitöltés */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 200px' }}>
          <Input placeholder="Keresés SKU, név alapján..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          <Input
            placeholder="Alapár ± %"
            value={bulkPercent}
            onChange={e => setBulkPercent(e.target.value)}
            type="number"
            style={{ width: 120 }}
          />
          <Button size="sm" variant="ghost" onClick={applyBulkPercent} disabled={!bulkPercent.trim() || rows.length === 0}>
            Kitöltés
          </Button>
        </div>
      </div>

      {/* Táblázat */}
      {loading ? (
        <div className="space-y-2">{[1,2,3,4,5].map(i => <div key={i} className="h-12 bg-gray-100 animate-pulse rounded" />)}</div>
      ) : rows.length === 0 ? (
        <EmptyState icon="📋" title="Nincs találat" description="Módosítsd a keresési feltételeket." />
      ) : (
        <div className="bg-white rounded-xl border border-[var(--color-border)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <th className="px-4 py-3">SKU</th>
                <th className="px-4 py-3">Megnevezés</th>
                <th className="px-4 py-3">Alapár</th>
                <th className="px-4 py-3" style={{ minWidth: 160 }}>Árlista-ár (Ft nettó)</th>
                <th className="px-4 py-3" style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const editVal = edits[row.item_id] ?? (row.fixed_net_price != null ? String(row.fixed_net_price) : '');
                const hasOverride = editVal !== '';
                const origVal = originals[row.item_id] ?? null;
                const parsedEdit = editVal === '' ? null : parseFloat(editVal);
                const isChanged = parsedEdit !== origVal;
                return (
                  <tr key={row.item_id} className="border-t border-gray-100 hover:bg-gray-50" style={isChanged ? { background: '#fffbeb' } : {}}>
                    <td className="px-4 py-2 font-mono text-xs text-gray-500">{row.sku}</td>
                    <td className="px-4 py-2 font-medium text-[#1a2540]">{row.item_name}</td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{formatP(row.base_price)}</td>
                    <td className="px-2 py-1">
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={editVal}
                        onChange={e => setEdit(row.item_id, e.target.value)}
                        placeholder={`alapár (${Math.round(row.base_price).toLocaleString('hu-HU')} Ft)`}
                        className="form-input"
                        style={{ width: '100%', fontSize: 13, padding: '5px 8px', color: hasOverride ? 'var(--navy)' : 'var(--slate)' }}
                        inputMode="decimal"
                      />
                    </td>
                    <td className="px-2 py-1 text-center">
                      {hasOverride && (
                        <button
                          onClick={() => clearEdit(row.item_id)}
                          title="Törlés (visszaáll alapárra)"
                          style={{ fontSize: 12, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                        >✕</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Lapozás */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 14 }}>
          <button onClick={() => goPage(page - 1)} disabled={page <= 1} style={{ fontSize: 13, padding: '4px 10px', border: '1px solid var(--line)', borderRadius: 6, background: '#fff', cursor: page > 1 ? 'pointer' : 'default', opacity: page > 1 ? 1 : 0.4 }}>←</button>
          <span style={{ fontSize: 13, color: 'var(--slate)' }}>{page} / {totalPages} oldal ({total} cikk)</span>
          <button onClick={() => goPage(page + 1)} disabled={page >= totalPages} style={{ fontSize: 13, padding: '4px 10px', border: '1px solid var(--line)', borderRadius: 6, background: '#fff', cursor: page < totalPages ? 'pointer' : 'default', opacity: page < totalPages ? 1 : 0.4 }}>→</button>
        </div>
      )}

      {/* Mentés gomb alul is (kényelmi) */}
      {dirty && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
          <Button onClick={handleSave} loading={saving}>Mentés</Button>
        </div>
      )}

      {/* Nem mentett változások figyelmeztetés */}
      {showLeaveModal && (
        <div className="overlay show" style={{ display: 'flex', padding: 20, zIndex: 200 }} onClick={() => setShowLeaveModal(false)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-h"><h3>Nem mentett változások</h3><button className="x" onClick={() => setShowLeaveModal(false)}>&times;</button></div>
            <div className="modal-b">
              <p style={{ fontSize: 14, color: 'var(--slate)', lineHeight: 1.6 }}>Az árlista-módosítások még nincsenek mentve. Ha visszamész, az összes változtatás elvész.</p>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--line)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="ghost" onClick={() => setShowLeaveModal(false)}>Mégsem</Button>
              <Button onClick={onBack} style={{ background: '#dc2626' }}>Eldobás és visszalépés</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Import Wizard (3-lépéses) ──

function ImportWizard({ onClose, onComplete }: { onClose: () => void; onComplete: () => void }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<{ imported: number; updated: number; errors: number } | null>(null);

  async function handleImport() {
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('accessToken');
      const res = await fetch('/api/items/import', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        setResult({ imported: data.imported || data.totalProcessed || 0, updated: data.updated || 0, errors: data.errors || 0 });
        setStep(3);
      }
    } catch { /* */ } finally { setImporting(false); }
  }

  return (
    <div className="overlay show" style={{display:"flex",padding:"20px"}} onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-h">
          <h3 className="">Cikkek importálása</h3>
          <button onClick={onClose} className="x">&times;</button>
        </div>
        <div className="modal-b">
          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {[1,2,3].map(s => <div key={s} className={`w-2.5 h-2.5 rounded-full ${s <= step ? 'bg-[#F47B20]' : 'bg-gray-300'}`} />)}
          </div>

          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Tölts fel egy CSV vagy XLSX fájlt. <button className="text-[#F47B20] hover:underline">Letöltheted a sablont innen.</button></p>
              <div
                className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-[#F47B20] transition-colors cursor-pointer"
                onClick={() => document.getElementById('import-file')?.click()}
                onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-[#F47B20]'); }}
                onDragLeave={e => e.currentTarget.classList.remove('border-[#F47B20]')}
                onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setFile(f); }}
              >
                <input id="import-file" type="file" accept=".csv,.xlsx" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) setFile(f); }} />
                {file ? (
                  <div><span className="text-lg">📄</span><p className="mt-2 font-medium">{file.name}</p><p className="text-xs text-gray-500">{(file.size / 1024).toFixed(0)} KB</p></div>
                ) : (
                  <div><span className="text-3xl text-gray-300">📂</span><p className="mt-2 text-sm text-gray-500">Húzd ide a fájlt vagy kattints a tallózásra</p><p className="text-xs text-gray-400 mt-1">Maximum 10 MB, 10 000 sor</p></div>
                )}
              </div>
              <div className="flex justify-end">
                <Button onClick={() => setStep(2)} disabled={!file}>Tovább →</Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Ellenőrizd a mező-megfeleltetéseket, majd indítsd az importot.</p>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="grid grid-cols-2 gap-2 font-medium text-gray-600"><span>Fájl-oszlop</span><span>Modulum-mező</span></div>
                {['SKU → SKU ✓', 'Megnevezés → Megnevezés ✓', 'Egységár → Nettó egységár ✓', 'ÁFA → ÁFA-kulcs ✓', 'Egység → Mértékegység ✓'].map(row => (
                  <div key={row} className="grid grid-cols-2 gap-2 text-gray-700">{row.split(' → ').map((c, i) => <span key={i}>{c}</span>)}</div>
                ))}
              </div>
              <div className="flex justify-between">
                <Button variant="ghost" onClick={() => setStep(1)}>← Vissza</Button>
                <Button onClick={handleImport} loading={importing}>Importálás indítása</Button>
              </div>
            </div>
          )}

          {step === 3 && result && (
            <div className="space-y-4 text-center">
              <div className="text-4xl">✅</div>
              <h4 className="">Import befejezve.</h4>
              <div className="text-sm text-gray-700 space-y-1">
                <p>{result.imported} cikk importálva</p>
                {result.updated > 0 && <p>{result.updated} cikk frissítve (SKU-egyezés)</p>}
                {result.errors > 0 && <p className="text-red-600">{result.errors} sor hibás, kihagyva</p>}
              </div>
              <Button onClick={onComplete}>Bezár</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
